import { useValue } from "@legendapp/state/react";
import { type BaseIconName, Icon } from "@repo/ui/components/icon";
import { Token } from "@repo/ui/components/token";
import { useSidebarMobileClick } from "@repo/ui/hooks/use-sidebar-mobile-click";
import { Badge } from "@repo/ui/ui/badge";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@repo/ui/ui/sidebar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui/ui/tooltip";
import { createLink } from "@tanstack/react-router";
import { Fragment } from "react/jsx-runtime";

import { connections$ } from "~/stores/connections";
import { datasources$ } from "~/stores/datasources";
import { activeOrganization$ } from "~/stores/organizations";

const SidebarMenuButtonLink = createLink(SidebarMenuButton);

type SidebarItem = {
  label: string;
  icon: BaseIconName;
  to: string;
  exact?: boolean;
  /** Selector function for reactive count - use () => Object.keys(store$.get()).length */
  count$?: () => number;
  /** When true, a "Beta" badge is shown to signal the feature may still change. */
  beta?: boolean;
};

type SidebarSection = {
  label: string;
  /** Explanatory copy shown in a tooltip when hovering the section header. */
  tooltip?: string;
  items: SidebarItem[];
};

/** Wrapper component to enable reactive count observation inside map */
const SidebarSettingsItem = ({
  item,
  onClick,
}: {
  item: SidebarItem;
  onClick: () => void;
}) => {
  const count = useValue(item.count$);

  return (
    <SidebarMenuItem>
      <SidebarMenuButtonLink
        name="left"
        to={item.to}
        activeOptions={{ exact: item.exact }}
        activeProps={{ isActive: true }}
        onClick={onClick}
      >
        <Icon name={item.icon} className="text-muted-foreground" />
        {item.label}
        {item.beta ? (
          <Badge variant="secondary" className="-mr-0.5 ml-auto">
            Beta
          </Badge>
        ) : (
          <Token className="-mr-0.5 ml-auto">{count}</Token>
        )}
      </SidebarMenuButtonLink>
    </SidebarMenuItem>
  );
};

const PERSONAL_SECTION: SidebarSection = {
  label: "Personal",
  tooltip: "Your personal account settings.",
  items: [
    {
      label: "Account",
      icon: "person-circle",
      to: "/settings",
      exact: true,
    },
  ],
};

const ORGANIZATION_SECTION: SidebarSection = {
  label: "Organization",
  tooltip: "Settings and members for your organization.",
  items: [
    {
      label: "General",
      icon: "building",
      to: "/settings/organization",
    },
    {
      label: "Members",
      icon: "person-plus",
      to: "/settings/members",
    },
    {
      label: "Datasources",
      icon: "stack-rounded",
      to: "/settings/datasources",
      count$: () => Object.keys(datasources$.get() ?? {}).length,
    },
    {
      label: "Connections",
      icon: "plug",
      to: "/settings/connections",
      count$: () => Object.keys(connections$.get() ?? {}).length,
    },
    {
      label: "Knowledge",
      icon: "book",
      to: "/settings/knowledge",
    },
    {
      label: "API & MCP",
      icon: "command-line",
      to: "/settings/api-keys",
      beta: true,
    },
    {
      label: "Export",
      icon: "document-arrow-down",
      to: "/settings/export",
    },
    {
      label: "Plans",
      icon: "card",
      to: "/settings/plans",
    },
  ],
};

export const SidebarSettings = () => {
  const handleLinkClick = useSidebarMobileClick("left");
  const hasActiveOrg = useValue(activeOrganization$.id);

  const sections = hasActiveOrg
    ? [PERSONAL_SECTION, ORGANIZATION_SECTION]
    : [PERSONAL_SECTION];

  return (
    <SidebarGroup>
      {sections.map((section) => (
        <Fragment key={section.label}>
          <SidebarGroupLabel>
            {section.tooltip ? (
              <Tooltip>
                <TooltipTrigger render={<span className="select-none" />}>
                  {section.label}
                </TooltipTrigger>
                <TooltipContent side="right">{section.tooltip}</TooltipContent>
              </Tooltip>
            ) : (
              section.label
            )}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {section.items.map((item) => (
                <SidebarSettingsItem
                  key={item.label}
                  item={item}
                  onClick={handleLinkClick}
                />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </Fragment>
      ))}
    </SidebarGroup>
  );
};
